import { Link } from "react-router-dom";
import { Smile, Eye, Shield, HeartHandshake, Activity, LifeBuoy, Wallet, Receipt, CheckCircle, ArrowRight, ClipboardList, BookOpen, HandHeart, Sparkles, PiggyBank, TrendingDown, Users, Zap } from "lucide-react";
import { LimitedTimeOfferSection } from "../components/LimitedTimeOfferSection";
import { usePageMeta } from "../hooks/usePageMeta";

const scrollToOffer = () => {
  const el = document.querySelector('[data-testid="limited-time-offer-section"]');
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const services = [
  {
    icon: Smile, title: "Dental Insurance",
    image: "https://images.unsplash.com/photo-1675526607070-f5cbd71dde92?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwxfHxoYXBweSUyMHdvbWFuJTIwc21pbGluZyUyMHdoaXRlJTIwdGVldGglMjBuYXR1cmFsJTIwcG9ydHJhaXR8ZW58MHx8fHwxNzcyNzI1NTY2fDA&ixlib=rb-4.1.0&q=85",
    description: "Comprehensive dental coverage helps your employees maintain their oral health. From cleanings to major procedures, we find plans that fit your budget.",
    benefits: ["Preventive care coverage", "Flexible deductibles", "Large network of providers", "No waiting periods on many plans"],
  },
  {
    icon: Eye, title: "Vision Insurance",
    image: "https://customer-assets.emergentagent.com/job_small-biz-insurance/artifacts/c3pyrfra_3KuhN95g6kpy8yMXGHGgVgHx.jpg",
    containImage: true,
    description: "Help your employees see clearly with affordable vision insurance. Cover eye exams, glasses, and contacts, keeping your workforce focused and productive.",
    benefits: ["Annual eye exams covered", "Glasses & contact lens allowances", "Wide network of optometrists", "Affordable monthly premiums"],
  },
  {
    icon: Shield, title: "Supplemental Insurance", image: null,
    description: "Life is unpredictable. Supplemental insurance gives your employees an extra financial safety net for accidents, critical illness, or unexpected events.",
    benefits: ["Accident insurance", "Critical illness coverage", "Hospital indemnity plans", "Life insurance options"],
  },
  {
    icon: HeartHandshake, title: "Group Life Insurance", image: null,
    description: "Give your team peace of mind with group life insurance. Affordable, employer sponsored coverage that protects your employees' families when it matters most.",
    benefits: ["Employer paid or voluntary options", "Guaranteed issue amounts available", "Coverage for spouses and dependents", "Easy payroll deduction"],
    offerBadge: true,
  },
  {
    icon: Activity, title: "Short Term Disability", image: null,
    description: "When an employee can't work due to injury or illness, short term disability replaces a portion of their income so recovery does not become a financial crisis.",
    benefits: ["Income replacement during recovery", "Coverage for injury, illness, or maternity", "Quick claim processing", "Affordable group rates"],
  },
  {
    icon: LifeBuoy, title: "Long Term Disability", image: null,
    description: "Long term disability protects employees facing extended time away from work. It bridges the gap until they can return, or transitions to retirement if they can't.",
    benefits: ["Coverage that lasts months or years", "Replaces a percentage of income", "Pairs seamlessly with short term disability", "Strong retention benefit for key employees"],
  },
  {
    icon: Receipt, title: "ICHRA (Health Reimbursement)", image: null,
    description: "An Individual Coverage HRA lets you reimburse employees tax-free for the individual health plans they choose themselves. A flexible, predictable alternative to traditional group health insurance — especially powerful for small and growing businesses.",
    benefits: ["You set a fixed monthly budget", "Tax-free reimbursements to employees", "Employees pick the plan that fits them", "Predictable costs, no premium surprises"],
  },
  {
    icon: Wallet, title: "Pre-Tax Spending Accounts", image: null,
    description: "Help your employees stretch their paycheck further with tax-advantaged accounts. We offer HSAs, FSAs, Dependent Care Accounts, and Commuter Benefits — real savings for them, real tax advantages for you.",
    benefits: ["Health Savings Accounts (HSA)", "Flexible Spending Accounts (FSA)", "Dependent Care Accounts (DCA)", "Commuter & transit benefits"],
  },
];

const enrollmentCards = [
  {
    icon: ClipboardList,
    title: "Painless Employee Enrollment",
    desc: "Enrolling employees in benefits does not have to be a headache. We manage the entire enrollment process for you, handling paperwork, deadlines, and coordination so you can focus on running your business.",
  },
  {
    icon: BookOpen,
    title: "Clear Benefits Explanation",
    desc: "Insurance can be confusing but it does not have to be. We take the guesswork out of understanding benefits by walking your employees through exactly what they are covered for, in plain language.",
  },
  {
    icon: HandHeart,
    title: "Support When It Matters Most",
    desc: "When life gets difficult and a claim needs to be filed, we do not disappear. We are here to walk your employees through the claims process step by step, providing guidance during some of their toughest moments.",
  },
];

// Sub-components
const employerBenefits = [
  {
    icon: PiggyBank,
    title: "Payroll Tax Savings",
    desc: "You save 7.65% in FICA payroll taxes on every dollar your employees contribute pre-tax. Real, ongoing savings to your bottom line.",
  },
  {
    icon: TrendingDown,
    title: "Lower Healthcare Costs",
    desc: "Pairing HSAs with high-deductible health plans lets you offer competitive coverage at significantly lower premium costs.",
  },
  {
    icon: Users,
    title: "Stronger Recruitment & Retention",
    desc: "These accounts are increasingly expected by candidates and show you offer a sophisticated, employee-friendly benefits package.",
  },
  {
    icon: Zap,
    title: "We Handle the Admin",
    desc: "Setup, enrollment, payroll integration, and employee education — we take it off your plate so you can focus on running your business.",
  },
  {
    icon: HeartHandshake,
    title: "Happier Employees",
    desc: "Employees keep more of their paycheck without you spending a dollar more. It's a real raise that costs you nothing.",
  },
];

const EmployerBenefitCard = ({ benefit }) => (
  <div
    data-testid={`employer-benefit-${benefit.title}`}
    className="group bg-white rounded-2xl p-7 border border-gray-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all"
  >
    <div className="w-14 h-14 bg-[#EAF9F9] rounded-2xl flex items-center justify-center mb-5 group-hover:bg-[#2EC4C4] transition-colors">
      <benefit.icon size={26} className="text-[#2EC4C4] group-hover:text-white transition-colors" />
    </div>
    <h3 className="font-bold text-lg text-[#1A2744] mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>
      {benefit.title}
    </h3>
    <p className="text-[#475569] text-sm leading-relaxed">{benefit.desc}</p>
  </div>
);

const EmployerBenefitsSection = () => (
  <section data-testid="employer-pretax-benefits" className="py-20 bg-white">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-12 max-w-3xl mx-auto">
        <div className="inline-flex items-center space-x-2 bg-[#EAF9F9] text-[#2EC4C4] font-semibold text-xs uppercase tracking-wider px-4 py-2 rounded-full mb-5">
          <Wallet size={14} />
          <span>For Employers</span>
        </div>
        <h2 className="font-bold text-3xl lg:text-4xl text-[#1A2744] mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
          Why Pre-Tax Accounts Make Sense for Your Business
        </h2>
        <p className="text-[#475569] text-lg leading-relaxed">
          HSAs, FSAs, and Dependent Care Accounts are not just a perk for your employees. They are one of the most efficient, lowest-cost ways to strengthen your benefits package while reducing your tax burden.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
        {employerBenefits.map((b) => <EmployerBenefitCard key={b.title} benefit={b} />)}
      </div>

      {/* Tax savings example */}
      <div className="bg-gradient-to-br from-[#1A2744] to-[#0F1B33] rounded-2xl p-8 lg:p-10 text-center max-w-4xl mx-auto">
        <p className="text-[#2EC4C4] text-xs font-bold uppercase tracking-wider mb-3">Quick Math Example</p>
        <p className="text-white text-lg lg:text-xl leading-relaxed mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
          If 20 employees each contribute $2,000 pre-tax to an FSA or HSA,
          <span className="text-[#2EC4C4] font-bold"> you save roughly $3,060 in payroll taxes every year</span>
          {" "}— at no additional cost to your business.
        </p>
        <p className="text-gray-400 text-xs">Illustrative example. Your actual savings depend on participation and contribution levels.</p>
      </div>
    </div>
  </section>
);

const EnrollmentCard = ({ card }) => (
  <div className="bg-[#F8FAFC] rounded-2xl p-8 border border-gray-100 hover:shadow-md hover:-translate-y-1 transition-all">
    <div className="w-14 h-14 bg-[#EAF9F9] rounded-2xl flex items-center justify-center mb-5">
      <card.icon size={28} className="text-[#2EC4C4]" />
    </div>
    <h3 className="font-bold text-xl text-[#1A2744] mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>{card.title}</h3>
    <p className="text-[#475569] leading-relaxed">{card.desc}</p>
  </div>
);

const ServiceDetailCard = ({ service }) => (
  <div
    data-testid={`service-detail-card-${service.title}`}
    className={`relative bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all overflow-hidden ${service.featured ? "lg:col-span-2" : ""} ${service.offerBadge ? "ring-2 ring-[#2EC4C4]/40" : ""}`}
  >
    {service.offerBadge && (
      <button
        type="button"
        onClick={scrollToOffer}
        data-testid="group-life-offer-badge"
        className="absolute top-4 right-4 z-10 inline-flex items-center space-x-1.5 bg-[#2EC4C4] hover:bg-[#25A0A0] text-white text-xs font-semibold uppercase tracking-wider px-3 py-1.5 rounded-full shadow-md transition-all hover:shadow-lg active:scale-95"
        aria-label="View limited time offer details"
      >
        <Sparkles size={12} />
        <span>Limited Time Offer</span>
      </button>
    )}
    <div className={`grid gap-0 ${service.featured && service.image ? "grid-cols-1 lg:grid-cols-2" : "grid-cols-1"}`}>
      {service.image && (
        <div className={`overflow-hidden bg-white ${service.featured ? "h-56 lg:h-auto" : "h-48"}`}>
          <img src={service.image} alt={service.title} className={`w-full h-full ${service.containImage ? "object-contain" : "object-cover"}`} />
        </div>
      )}
      <div className="p-8">
        <div className="flex items-center space-x-3 mb-4">
          <div className="w-12 h-12 bg-[#EAF9F9] rounded-xl flex items-center justify-center flex-shrink-0">
            <service.icon size={24} className="text-[#2EC4C4]" />
          </div>
          <h3 className="font-bold text-xl text-[#1A2744]" style={{ fontFamily: "'Outfit', sans-serif" }}>{service.title}</h3>
        </div>
        <p className="text-[#475569] text-sm leading-relaxed mb-5">{service.description}</p>
        <ul className="space-y-2">
          {service.benefits.map((b) => (
            <li key={b} className="flex items-center space-x-2">
              <CheckCircle size={16} className="text-[#2EC4C4] flex-shrink-0" />
              <span className="text-[#0F172A] text-sm">{b}</span>
            </li>
          ))}
        </ul>
        {service.offerBadge && (
          <button
            type="button"
            onClick={scrollToOffer}
            data-testid="group-life-offer-link"
            className="mt-5 inline-flex items-center space-x-1.5 text-[#2EC4C4] hover:text-[#25A0A0] font-semibold text-sm transition-colors"
          >
            <span>See $10,000 Limited Time Offer</span>
            <ArrowRight size={14} />
          </button>
        )}
      </div>
    </div>
  </div>
);

export default function ServicesPage() {
  usePageMeta({
    title: "Insurance Services | Dental, Vision, Life, Disability, ICHRA, HSAs & FSAs for ND Businesses",
    description: "Employee benefits services in North Dakota: dental, vision, supplemental, group life, disability, ICHRA health reimbursement plans, plus pre-tax spending accounts including HSAs, FSAs, and Dependent Care Accounts.",
    path: "/services",
  });
  return (
    <div className="pt-16 lg:pt-20">
      <section data-testid="services-hero" className="bg-[#1A2744] py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="font-bold text-4xl sm:text-5xl text-white mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>Our Services</h1>
          <p className="text-gray-300 text-lg max-w-2xl mx-auto">Comprehensive employee benefits solutions designed for small businesses in North Dakota and beyond.</p>
        </div>
      </section>

      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-[#475569] text-lg leading-relaxed">We believe every employee deserves quality benefits, and every small business deserves a fair shot at providing them. Browse our offerings below — then let us put together a package that works for you.</p>
        </div>
      </section>

      <section data-testid="enrollment-section" className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="font-bold text-3xl lg:text-4xl text-[#1A2744] mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>We Handle the Hard Parts</h2>
            <p className="text-[#475569] text-lg max-w-2xl mx-auto">From enrollment to explanation, we're with you and your employees every step of the way.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {enrollmentCards.map((card) => <EnrollmentCard key={card.title} card={card} />)}
          </div>
        </div>
      </section>

      <section data-testid="services-grid" className="pb-20 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {services.map((service) => <ServiceDetailCard key={service.title} service={service} />)}
          </div>
        </div>
      </section>

      <EmployerBenefitsSection />

      <LimitedTimeOfferSection />

      <section data-testid="services-cta" className="py-20 bg-[#1A2744]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-bold text-3xl lg:text-4xl text-white mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>Not Sure Where to Start?</h2>
          <p className="text-gray-300 text-lg mb-8">We'll help you find the right combination of benefits for your business and budget. No pressure — just honest guidance.</p>
          <Link to="/contact" data-testid="services-cta-btn" className="inline-flex items-center space-x-2 bg-[#2EC4C4] hover:bg-[#25A0A0] text-white font-semibold rounded-lg px-8 py-4 text-base transition-all shadow-md hover:shadow-lg active:scale-95">
            <span>Request a Free Consultation</span><ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
}
